/*
 * Copyright (c) 2024 Étienne FOLIO
 * Tous droits réservés.
 *
 * Ce fichier fait partie d'un logiciel propriétaire.
 * Toute reproduction, modification ou distribution, totale ou partielle,
 * de ce fichier est interdite sans l'autorisation écrite préalable
 * de l'auteur.
 */

// App.js

import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Spinner,
  Navbar,
  Nav,
  NavDropdown,
  Modal,
  Button,
} from 'react-bootstrap';
import api, {
  onSessionExpired,
  clearSessionExpiredState,
  probeSessionAlive,
} from './components/api';
import './App.css';
import { loadStoredTheme } from './themeConfig';
import ErrorBoundary from './ErrorBoundary';
import baseLists from './baseLists';
import { buildAclRightKey, hasAcl, hasAnyAcl } from './utils/acl';
import CollaboraWrapper from './components/CollaboraWrapper';

loadStoredTheme();

// Lazy‑loaded components
const Dashboard   = lazy(() => import('./components/Dashboard'));
const Treatment = lazy(() => import('./components/Treatment'));
const GenericList = lazy(() => import('./components/GenericList'));
const ConfigSettings = lazy(() => import('./components/ConfigSettings'));
const ThemeSettings = lazy(() => import('./components/ThemeSettings'));
const MyProfile = lazy(() => import('./components/MyProfile'));

/** Menu config **/

const sanitizeNextPath = (rawNext) => {
  const nextPath = String(rawNext || '').trim();
  if (!nextPath.startsWith('/')) {
    return '/';
  }
  if (nextPath.startsWith('//')) {
    return '/';
  }
  if (nextPath.includes('://')) {
    return '/';
  }
  return nextPath;
};

const COUNTS_CACHE_TTL_MS = 5000;
let countsCachePayload = null;
let countsCacheUpdatedAt = 0;
let pendingCountsRequest = null;

const normalizeCountsPayload = (payload) =>
  payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};

const areCountObjectsEqual = (left, right) => {
  if (left === right) {
    return true;
  }
  const leftObject = left && typeof left === 'object' ? left : {};
  const rightObject = right && typeof right === 'object' ? right : {};
  const leftKeys = Object.keys(leftObject);
  const rightKeys = Object.keys(rightObject);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  return leftKeys.every((key) => leftObject[key] === rightObject[key]);
};

const fetchCountsPayload = async (forceRefresh = false) => {
  const now = Date.now();
  if (
    !forceRefresh &&
    countsCachePayload &&
    now - countsCacheUpdatedAt < COUNTS_CACHE_TTL_MS
  ) {
    return countsCachePayload;
  }

  if (!forceRefresh && pendingCountsRequest) {
    return pendingCountsRequest;
  }

  pendingCountsRequest = api
    .get('counts_de_tables/')
    .then(({ data }) => {
      const normalized = normalizeCountsPayload(data);
      countsCachePayload = normalized;
      countsCacheUpdatedAt = Date.now();
      return normalized;
    })
    .finally(() => {
      pendingCountsRequest = null;
    });

  return pendingCountsRequest;
};

/** Login page **/
function LoginPage({ siteCfg }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode]   = useState('');
  const [error, setError]       = useState('');
  const location                = useLocation();
  const nextUrl                 = sanitizeNextPath(
    new URLSearchParams(location.search).get('next')
  );
  const mfaCodeLabel            = siteCfg?.texts?.auth?.login_mfa_code_label || 'Code MFA';

  const handleLogin = async e => {
    e.preventDefault();
    try {
      await api.post('login/', { username, password, otp_code: mfaCode });
      // une fois connecté, redirige vers nextUrl (ou '/')
      window.location.href = nextUrl;
    } catch (error) {
      const apiDetail = error?.response?.data?.detail;
      setError(
        typeof apiDetail === 'string' && apiDetail
          ? apiDetail
          : siteCfg?.texts?.auth?.login_error || 'Identifiants invalides',
      );
    }
  };

  return (
    <Container className="mt-5">
      <h2>{siteCfg?.texts?.auth?.login_title || 'Connexion'}</h2>
      {error && <p className="text-danger">{error}</p>}
      <form onSubmit={handleLogin}>
        <div className="mb-3">
          <label>{siteCfg?.texts?.auth?.login_username || "Nom d'utilisateur"}</label>
          <input
            type="text"
            className="form-control"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label>{siteCfg?.texts?.auth?.login_password || 'Mot de passe'}</label>
          <input
            type="password"
            className="form-control"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label>{mfaCodeLabel}</label>
          <input
            type="text"
            className="form-control"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            autoComplete="one-time-code"
          />
        </div>
        <button className="btn btn-primary">{siteCfg?.texts?.auth?.login_button || 'Se connecter'}</button>
      </form>
    </Container>
  );
}

/** PrivateRoute wrapper **/
function PrivateRoute({ authenticated }) {
  const location = useLocation();
  return authenticated
    ? <Outlet/>
    : <Navigate to={`/login?next=${location.pathname}`} replace />;
}

function LegacyTableRedirect({ tableKeys }) {
  const { tableName } = useParams();

  if (tableKeys.includes(tableName)) {
    return <Navigate to={`/saisie/${tableName}`} replace />;
  }

  return <Navigate to="/" replace />;
}

function SaisieTableRoute({
  isLoadingUser,
  tableKeys,
  fallbackPath,
  actualise,
  siteConfig,
  currentUser,
}) {
  const { tableName } = useParams();

  if (isLoadingUser) {
    return (
      <div className="text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!tableKeys.includes(tableName)) {
    return <Navigate to={fallbackPath || '/'} replace />;
  }

  return (
    <GenericList
      actualise={actualise}
      siteConfig={siteConfig}
      currentUser={currentUser}
    />
  );
}

/** Authenticated application shell **/
function AuthenticatedApp({ siteCfg }) {
  const [user, setUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [counts, setCounts] = useState({});
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionUsername, setSessionUsername] = useState('');
  const [sessionPassword, setSessionPassword] = useState('');
  const [sessionMfaCode, setSessionMfaCode] = useState('');
  const [sessionError, setSessionError] = useState('');
  const [isSessionSubmitting, setIsSessionSubmitting] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [releaseNotesHtml, setReleaseNotesHtml] = useState('');
  const [releaseNotesLoading, setReleaseNotesLoading] = useState(false);
  const [releaseNotesError, setReleaseNotesError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const openSessionReconnectModal = useCallback(() => {
    setSessionError('');
    setSessionPassword('');
    setSessionMfaCode('');
    setSessionUsername(user?.username || '');
    setShowSessionModal(true);
  }, [user?.username]);

  // 1) Récupère l'utilisateur courant
  useEffect(() => {
    setIsLoadingUser(true);
    api.get('current-user/')
      .then(res => {
        setUser(res.data);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setIsLoadingUser(false);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      openSessionReconnectModal();
    });
    return unsubscribe;
  }, [openSessionReconnectModal]);

  const ensureSessionBeforeNavigation = useCallback(async () => {
    try {
      await probeSessionAlive();
      return true;
    } catch {
      openSessionReconnectModal();
      return false;
    }
  }, [openSessionReconnectModal]);

  const navigateWithSessionCheck = useCallback(
    async (targetPath) => {
      if (!targetPath || targetPath === location.pathname) {
        return;
      }
      const isSessionOk = await ensureSessionBeforeNavigation();
      if (!isSessionOk) {
        return;
      }
      navigate(targetPath);
    },
    [ensureSessionBeforeNavigation, location.pathname, navigate],
  );

  const rawTableEntries = useMemo(() => {
    const hiddenSaisieKeys = new Set(['user', 'service_radio', 'association']);
    const entries = [];
    (siteCfg?.menu || Object.keys(baseLists)).forEach((item) => {
      const key = item.key || item;
      if (hiddenSaisieKeys.has(key)) {
        return;
      }
      const base = baseLists[key] || { path: `/${key}`, label: key };
      const label = item.label || base.label;
      const visible = item.visible !== false;
      if (visible) {
        entries.push({ key, label, showBadge: base.showBadge });
      }
    });
    return entries;
  }, [siteCfg?.menu]);

  const canReadYearStatuses = hasAcl(user, 'api.annee_statuts.read');
  const canReadLetters = hasAcl(user, 'api.letter_exports.read');
  const canReadNotes = hasAcl(user, 'api.note_exports.read');
  const canReadReleaseNotes = hasAcl(user, 'api.release_notes.read');
  const canReadTableCounts = hasAcl(user, 'api.counts_de_tables.read');
  const canAccessConfigSettings = hasAnyAcl(user, [
    'api.ui_config.read',
    'api.ui_config.write',
    'api.ui_config.tabs.saisie_tables',
    'api.ui_config.tabs.table_config',
    'api.ui_config.tabs.system',
    'api.ui_config.tabs.documents',
    'api.ui_config.tabs.texts',
    'api.ui_config.tabs.accounts',
    'api.ui_config.tabs.organizations',
    'api.ui_config.tabs.acl',
    'api.ui_config.tabs.backup_restore',
    'api.ui_config.tabs.storage',
    'api.system_backup.manage',
    'api.auth_users.read',
    'api.auth_users.create',
    'api.auth_users.update',
    'api.auth_users.delete',
    'api.auth_groups.read',
    'api.auth_groups.create',
    'api.auth_groups.update',
    'api.auth_groups.delete',
    'api.document_templates.preview',
    'api.document_templates.history',
    'api.acl.manage',
  ]);

  const tableEntries = useMemo(
    () =>
      rawTableEntries.filter((entry) =>
        hasAcl(user, buildAclRightKey(entry.key, 'read')),
      ),
    [rawTableEntries, user],
  );

  const tableKeys = useMemo(() => tableEntries.map((entry) => entry.key), [tableEntries]);
  const defaultSaisiePath = tableEntries.length > 0 ? `/saisie/${tableEntries[0].key}` : '/';
  const defaultTraitementPath = canReadYearStatuses
    ? '/traitement'
    : canReadLetters
    ? '/traitement/lettres'
    : canReadNotes
    ? '/traitement/notes'
    : '/';
  const canAccessTraitement = hasAnyAcl(user, [
    'api.annee_statuts.read',
    'api.letter_exports.read',
    'api.note_exports.read',
  ]);
  const isSaisieRoute = location.pathname.startsWith('/saisie');
  const isTraitementRoute = location.pathname.startsWith('/traitement');
  const isSaisieActive = isSaisieRoute && tableEntries.length > 0;
  const isTraitementActive = isTraitementRoute && canAccessTraitement;

  // 2) Récupère les counts pour les badges
  const fetchCounts = useCallback(async (forceRefresh = false) => {
    try {
      const nextCounts = await fetchCountsPayload(Boolean(forceRefresh));
      setCounts((previousCounts) =>
        areCountObjectsEqual(previousCounts, nextCounts)
          ? previousCounts
          : nextCounts
      );
    } catch {}
  }, []);

  const refreshCounts = useCallback(() => {
    void fetchCounts(true);
  }, [fetchCounts]);

  useEffect(() => {
    if (canReadTableCounts) {
      fetchCounts();
      return;
    }
    setCounts({});
  }, [canReadTableCounts, fetchCounts]);

  const handleUserUpdated = useCallback((payload) => {
    const normalizedPayload = payload && typeof payload === 'object' ? payload : {};
    setUser((prev) => {
      if (!prev || typeof prev !== 'object') {
        return prev;
      }

      const nextEmail =
        typeof normalizedPayload.email === 'string'
          ? normalizedPayload.email
          : prev.email;
      const nextFirstName =
        typeof normalizedPayload.first_name === 'string'
          ? normalizedPayload.first_name
          : prev.first_name;
      const nextLastName =
        typeof normalizedPayload.last_name === 'string'
          ? normalizedPayload.last_name
          : prev.last_name;

      if (
        nextEmail === prev.email &&
        nextFirstName === prev.first_name &&
        nextLastName === prev.last_name
      ) {
        return prev;
      }

      return {
        ...prev,
        email: nextEmail,
        first_name: nextFirstName,
        last_name: nextLastName,
      };
    });
  }, []);

  // 3) Logout
  const handleLogout = async () => {
    await api.post('logout/');
    clearSessionExpiredState();
    window.location.href = '/login';
  };

  const handleSessionReconnect = async (event) => {
    event.preventDefault();
    setSessionError('');
    setIsSessionSubmitting(true);
    try {
      await api.post('login/', {
        username: sessionUsername,
        password: sessionPassword,
        otp_code: sessionMfaCode,
      });
      clearSessionExpiredState();
      setShowSessionModal(false);
      setSessionPassword('');
      setSessionMfaCode('');
      // Ne force pas de rechargement global ici pour conserver l'état
      // du formulaire/écran courant après reconnexion.
      const currentUser = await api.get('current-user/');
      setUser(currentUser.data);
    } catch (error) {
      const apiDetail = error?.response?.data?.detail;
      setSessionError(
        typeof apiDetail === 'string' && apiDetail
          ? apiDetail
          : siteCfg?.texts?.auth?.login_error || 'Identifiants invalides',
      );
    } finally {
      setIsSessionSubmitting(false);
    }
  };

  const feedbackHref =
    siteCfg?.texts?.system?.admin_feedback_button_href ||
    'https://project.recordandlive.fr/projects/fser-repport/work_packages';
  const feedbackLabel =
    siteCfg?.texts?.system?.admin_feedback_button_label || 'Faire un retour';
  const themeSettingsMenuLabel =
    siteCfg?.texts?.system?.theme_settings_menu_label || 'Paramétrage du thème';
  const adminSettingsMenuLabel =
    siteCfg?.texts?.system?.admin_settings_menu_label || "Paramètres de l'application";
  const userMenuItemClassName = 'd-flex align-items-center gap-2';
  const profileMenuLabel =
    siteCfg?.texts?.auth?.profile_menu_item_label || 'Mon profil';

  const handleFeedbackClick = () => {
    window.open(feedbackHref, '_blank', 'noopener,noreferrer');
  };

  const releaseNotesTitle =
    siteCfg?.texts?.system?.release_notes_title || 'Notes de version';
  const releaseNotesErrorMessage =
    siteCfg?.texts?.system?.release_notes_error ||
    'Impossible de charger les notes de version.';
  const releaseNotesCloseLabel =
    siteCfg?.texts?.system?.release_notes_close || 'Fermer';
  const loadingLabel =
    siteCfg?.texts?.system?.loading || 'Chargement…';
  const navSaisieLabel =
    siteCfg?.texts?.other?.workspace_nav_saisie || 'Saisie';
  const navTraitementLabel =
    siteCfg?.texts?.other?.workspace_nav_traitement || 'Traitement';
  const traitementMenuLettresLabel =
    siteCfg?.texts?.other?.workspace_traitement_letters ||
    siteCfg?.texts?.other?.dashboard_letters_title ||
    'Export des lettres';
  const traitementMenuOverviewLabel =
    siteCfg?.texts?.other?.workspace_traitement_overview ||
    'Statut des années';
  const traitementMenuNotesLabel =
    siteCfg?.texts?.other?.workspace_traitement_notes ||
    siteCfg?.texts?.other?.dashboard_notes_title ||
    "Notes d'activité";
  const saisieEmptyLabel =
    siteCfg?.texts?.other?.workspace_saisie_empty ||
    'Aucune table configurée.';
  const loginTitle =
    siteCfg?.texts?.auth?.login_title || 'Connexion';
  const loginUsernameLabel =
    siteCfg?.texts?.auth?.login_username || "Nom d'utilisateur";
  const loginPasswordLabel =
    siteCfg?.texts?.auth?.login_password || 'Mot de passe';
  const loginMfaCodeLabel =
    siteCfg?.texts?.auth?.login_mfa_code_label || 'Code MFA';
  const loginButtonLabel =
    siteCfg?.texts?.auth?.login_button || 'Se connecter';

  const fetchReleaseNotes = async () => {
    setReleaseNotesLoading(true);
    setReleaseNotesError('');
    try {
      const response = await api.get('release-notes/');
      setReleaseNotesHtml(response?.data?.html || '');
    } catch (error) {
      setReleaseNotesHtml('');
      setReleaseNotesError(releaseNotesErrorMessage);
    } finally {
      setReleaseNotesLoading(false);
    }
  };

  const handleReleaseNotesBadgeClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!canReadReleaseNotes) {
      return;
    }

    setShowReleaseNotes(true);
    if (!releaseNotesHtml && !releaseNotesLoading) {
      fetchReleaseNotes();
    }
  };

  const handleReleaseNotesClose = () => {
    setShowReleaseNotes(false);
  };

  return (
    <div className="d-flex flex-column app-shell">
      {/* Navbar */}
      <Navbar bg="primary" variant="dark" expand="lg">
        <Container fluid>
          <Navbar.Brand
            role="button"
            className="d-flex align-items-center gap-2"
            onClick={() => {
              void navigateWithSessionCheck('/');
            }}
          >
            <span>
              {siteCfg?.texts?.config?.site_title || 'FSER_Repport'}
            </span>
            {siteCfg?.version ? (
              canReadReleaseNotes ? (
                <button
                  type="button"
                  className="badge bg-success border-0"
                  onClick={handleReleaseNotesBadgeClick}
                  title={releaseNotesTitle}
                >
                  {siteCfg.version}
                </button>
              ) : (
                <span
                  className="badge bg-success"
                  style={{ cursor: 'default' }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {siteCfg.version}
                </span>
              )
            ) : null}
          </Navbar.Brand>
          <Nav className="me-auto ms-3">
            {tableEntries.length > 0 ? (
              <Nav.Item>
                <Nav.Link
                  href={defaultSaisiePath}
                  active={isSaisieActive}
                  onClick={(event) => {
                    event.preventDefault();
                    void navigateWithSessionCheck(defaultSaisiePath);
                  }}
                >
                  {navSaisieLabel}
                </Nav.Link>
              </Nav.Item>
            ) : null}
            {canAccessTraitement ? (
              <Nav.Item>
                <Nav.Link
                  href={defaultTraitementPath}
                  active={isTraitementActive}
                  onClick={(event) => {
                    event.preventDefault();
                    void navigateWithSessionCheck(defaultTraitementPath);
                  }}
                >
                  {navTraitementLabel}
                </Nav.Link>
              </Nav.Item>
            ) : null}
          </Nav>
          <Nav className="ms-auto align-items-center">
            <NavDropdown
              align="end"
              title={user?.first_name || user?.username || 'Utilisateur'}
            >
              <NavDropdown.Item
                onClick={() => {
                  void navigateWithSessionCheck('/theme');
                }}
                className={userMenuItemClassName}
              >
                <span aria-hidden="true" className="fs-5">🎨</span>
                <span>{themeSettingsMenuLabel}</span>
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => {
                  void navigateWithSessionCheck('/profile');
                }}
                className={userMenuItemClassName}
              >
                <span aria-hidden="true" className="fs-5">👤</span>
                <span>{profileMenuLabel}</span>
              </NavDropdown.Item>
              {canAccessConfigSettings ? (
                <>
                  <NavDropdown.Item
                    onClick={handleFeedbackClick}
                    className={userMenuItemClassName}
                  >
                    <span aria-hidden="true" className="fs-5">💬</span>
                    <span>{feedbackLabel}</span>
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    onClick={() => {
                      void navigateWithSessionCheck('/config');
                    }}
                    className={userMenuItemClassName}
                  >
                    <span aria-hidden="true" className="fs-5">🛠️</span>
                    <span>{adminSettingsMenuLabel}</span>
                  </NavDropdown.Item>
                </>
              ) : null}
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={handleLogout}
                className={userMenuItemClassName}
              >
                <span aria-hidden="true" className="fs-5">🚪</span>
                <span>{siteCfg?.texts?.auth?.logout_button || 'Déconnexion'}</span>
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </Navbar>

      {/* Main layout */}
      <Container fluid className="flex-grow-1 p-0 app-main">
        <Row className="g-0 app-main-row">
          {/* Sidebar */}
          {isSaisieRoute ? (
            <Col
              xs="auto"
              className="bg-body text-body p-0 border-end border-body app-sidebar"
            >
              <ul className="list-group list-group-flush">
                {tableEntries.length > 0 ? (
                  tableEntries.map((entry) => {
                    const tablePath = `/saisie/${entry.key}`;
                    const isActive = location.pathname === tablePath;
                    return (
                      <li
                        key={entry.key}
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isActive ? 'active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          void navigateWithSessionCheck(tablePath);
                        }}
                      >
                        {entry.label}
                        {entry.showBadge && counts[entry.key] >= 0 ? (
                          <span
                            className={`badge rounded-pill ${isActive ? 'bg-light text-dark' : 'bg-primary'}`}
                          >
                            {counts[entry.key]}
                          </span>
                        ) : null}
                      </li>
                    );
                  })
                ) : (
                  <li className="list-group-item text-muted">{saisieEmptyLabel}</li>
                )}
              </ul>
            </Col>
          ) : null}
          {isTraitementRoute ? (
            <Col
              xs="auto"
              className="bg-body text-body p-0 border-end border-body app-sidebar"
            >
              <ul className="list-group list-group-flush">
                {canReadYearStatuses ? (
                  <li
                    className={`list-group-item list-group-item-action ${location.pathname === '/traitement' ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      void navigateWithSessionCheck('/traitement');
                    }}
                  >
                    {traitementMenuOverviewLabel}
                  </li>
                ) : null}
                {canReadLetters ? (
                  <li
                    className={`list-group-item list-group-item-action ${location.pathname === '/traitement/lettres' ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      void navigateWithSessionCheck('/traitement/lettres');
                    }}
                  >
                    {traitementMenuLettresLabel}
                  </li>
                ) : null}
                {canReadNotes ? (
                  <li
                    className={`list-group-item list-group-item-action ${location.pathname === '/traitement/notes' ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      void navigateWithSessionCheck('/traitement/notes');
                    }}
                  >
                    {traitementMenuNotesLabel}
                  </li>
                ) : null}
              </ul>
            </Col>
          ) : null}

          {/* Content area */}
          <Col id="table_zone" className="bg-body text-body px-4 pb-4 pt-0 app-content">
            <Suspense fallback={<div className="text-center"><Spinner animation="border"/></div>}>
              <Routes>
                <Route
                  path="/"
                  element={<Dashboard siteCfg={siteCfg} currentUser={user} />}
                />
                <Route
                  path="/traitement"
                  element={
                    canReadYearStatuses ? (
                      <Treatment
                        siteCfg={siteCfg}
                        section="traitement"
                        currentUser={user}
                      />
                    ) : (
                      <Navigate to={defaultTraitementPath} replace />
                    )
                  }
                />
                <Route
                  path="/traitement/lettres"
                  element={
                    canReadLetters ? (
                      <Treatment siteCfg={siteCfg} section="letters" currentUser={user} />
                    ) : (
                      <Navigate to={defaultTraitementPath} replace />
                    )
                  }
                />
                <Route
                  path="/traitement/notes"
                  element={
                    isLoadingUser || user === null ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        <div>{loadingLabel}</div>
                      </div>
                    ) : !canReadNotes ? (
                      <Navigate to={defaultTraitementPath} replace />
                    ) : (
                      <Treatment siteCfg={siteCfg} section="notes" currentUser={user} />
                    )
                  }
                />
                <Route
                  path="/saisie"
                  element={<Navigate to={defaultSaisiePath} replace />}
                />
                <Route
                  path="/saisie/:tableName"
                  element={
                    <SaisieTableRoute
                      isLoadingUser={isLoadingUser}
                      tableKeys={tableKeys}
                      fallbackPath={defaultSaisiePath}
                      actualise={refreshCounts}
                      siteConfig={siteCfg}
                      currentUser={user}
                    />
                  }
                />

                {/* Settings page */}
                <Route
                  path="/profile"
                  element={
                    isLoadingUser || user === null ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        {siteCfg?.texts?.system?.loading || 'Chargement…'}
                      </div>
                    ) : (
                      <MyProfile
                        siteCfg={siteCfg}
                        onUserUpdated={handleUserUpdated}
                      />
                    )
                  }
                />
                <Route
                  path="/config"
                  element={
                    isLoadingUser || user === null ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        {siteCfg?.texts?.system?.loading || 'Chargement…'}
                      </div>
                    ) : canAccessConfigSettings ? (
                      <ConfigSettings siteCfg={siteCfg} currentUser={user} />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
                <Route path="/config-admin" element={<Navigate to="/config" replace />} />

                {/* Theme settings page */}
                <Route
                  path="/theme"
                  element={<ThemeSettings siteCfg={siteCfg} />}
                />

                <Route path=":tableName" element={<LegacyTableRedirect tableKeys={tableKeys} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showSessionModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>{loginTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sessionError ? <p className="text-danger">{sessionError}</p> : null}
          <form onSubmit={handleSessionReconnect}>
            <div className="mb-3">
              <label>{loginUsernameLabel}</label>
              <input
                type="text"
                className="form-control"
                value={sessionUsername}
                onChange={(e) => setSessionUsername(e.target.value)}
                required
                disabled={isSessionSubmitting}
              />
            </div>
            <div className="mb-3">
              <label>{loginPasswordLabel}</label>
              <input
                type="password"
                className="form-control"
                value={sessionPassword}
                onChange={(e) => setSessionPassword(e.target.value)}
                required
                disabled={isSessionSubmitting}
              />
            </div>
            <div className="mb-3">
              <label>{loginMfaCodeLabel}</label>
              <input
                type="text"
                className="form-control"
                value={sessionMfaCode}
                onChange={(e) => setSessionMfaCode(e.target.value)}
                autoComplete="one-time-code"
                disabled={isSessionSubmitting}
              />
            </div>
            <Button type="submit" variant="primary" disabled={isSessionSubmitting}>
              {isSessionSubmitting ? loadingLabel : loginButtonLabel}
            </Button>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showReleaseNotes}
        onHide={handleReleaseNotesClose}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{releaseNotesTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {releaseNotesLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{loadingLabel}</span>
              </Spinner>
            </div>
          ) : releaseNotesError ? (
            <p className="text-danger mb-0">{releaseNotesError}</p>
          ) : (
            <div
              className="release-notes-content"
              dangerouslySetInnerHTML={{ __html: releaseNotesHtml }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleReleaseNotesClose}>
            {releaseNotesCloseLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/** Root App component **/
function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking]           = useState(true);
  const [siteCfg, setSiteCfg]             = useState({});
  const [siteCfgLoaded, setSiteCfgLoaded] = useState(false);

  useEffect(() => {
    api
      .get('ui-config/')
      .then(({ data }) => setSiteCfg(data))
      .catch(() => setSiteCfg({}))
      .finally(() => setSiteCfgLoaded(true));
  }, []);

  useEffect(() => {
    api.get('current-user/')
      .then((response) => {
        const isLoggedIn = Boolean(response?.data?.username);
        setAuthenticated(isLoggedIn);
      })
      .catch(() => setAuthenticated(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking || !siteCfgLoaded) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p>{siteCfg?.texts?.system?.checking_message || 'Vérification en cours…'}</p>
      </Container>
    );
  }

  return (
    <Router>
      <ErrorBoundary>
        <Routes>
          {/* Route publique login */}
          <Route path="/login" element={<LoginPage siteCfg={siteCfg}/>} />

          {/* Toutes les autres routes protégées */}
          <Route element={<PrivateRoute authenticated={authenticated}/>}>
            <Route
              path="/collabora-wrapper"
              element={<CollaboraWrapper siteCfg={siteCfg} />}
            />
            <Route path="/*" element={<AuthenticatedApp siteCfg={siteCfg}/>} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
